import type { Effect } from 'effector'
import { z } from 'zod'

import type { Duration } from '@/shared/lib/types'

import type { ChatAPIEffect } from './effect'
import type {
    HistoryResponse,
    LoginRequest,
    LoginResponse,
    MessageTranslateRequest,
    MessageTranslateResponse,
} from './protocol'

export type MessagePublishRequest = {
    channel: string
    message: string
    optimistic_id: string
    reply_message_id?: string
}

export type MessageReportRequest = {
    channel: string
    message_id: string
    reason_code: string
    reason_message: string
}

export type MessageDeleteRequest = {
    channel: string
    message_id: string

    ban_user: boolean
    report_spam: boolean
    delete_all_from_user: boolean
}

export type MessageReactionRequest = {
    channel: string
    message_id: string
    emoji: string
}

export type UserMuteRequest = {
    user_id: string
    message_id?: string
    reason_code: string
    reason_message: string
    expire_time: Date | null // null means "forever"
    hide_messages: boolean
}

export type UserUnmuteRequest = {
    user_id: string
    reason_message: string
}

export type HistoryRequest = {
    channel: string
    limit: number
}

export type ChannelJoinRequest = {
    channel: string
}

export type ChannelLeaveRequest = {
    channel: string
}

export type ChannelSlowmodeRequest = {
    channel: string
    cooldown: Duration | null
}

const ChatErrorCodeSchema = z.enum([
    'access_denied',
    'channel_not_found',
    'internal_error',
    'invalid_request',
    'maintenance',
    'message_not_found',
    'message_too_long',
    'slow_mode',
    'unauthorized',
    'user_muted',
])

type ChatErrorCode = z.infer<typeof ChatErrorCodeSchema>

export const ChatError = z.object({
    code: ChatErrorCodeSchema,
    message: z.string(),
})

export class ChatAPIError extends Error {
    public readonly code: ChatErrorCode | 'transport_error' | 'invalid_response' | 'unknown_error'

    constructor(error: { code: ChatAPIError['code']; message: string }, options?: ErrorOptions) {
        super(error.message, options)
        this.name = error.code
        this.code = error.code
        this.message = error.message
    }
}

export type HTTPResponse<T = any> = {
    data: T
    status: number
    headers: Record<string, string>
}

export type ChatAPI = {
    setToken: Effect<string | null, void>
    login: ChatAPIEffect<LoginRequest, LoginResponse>
    userMute: ChatAPIEffect<UserMuteRequest, void>
    userUnmute: ChatAPIEffect<UserUnmuteRequest, void>
    messagePublish: ChatAPIEffect<MessagePublishRequest, void>
    messageReport: ChatAPIEffect<MessageReportRequest, void>
    messageDelete: ChatAPIEffect<MessageDeleteRequest, void>
    messageReactionAdd: ChatAPIEffect<MessageReactionRequest, void>
    messageReactionRemove: ChatAPIEffect<MessageReactionRequest, void>
    messageTranslate: ChatAPIEffect<MessageTranslateRequest, MessageTranslateResponse>
    channelJoin: ChatAPIEffect<ChannelJoinRequest, void>
    channelLeave: ChatAPIEffect<ChannelLeaveRequest, void>
    channelHistory: ChatAPIEffect<HistoryRequest, HistoryResponse>
    channelSlowmodeSet: ChatAPIEffect<ChannelSlowmodeRequest, void>
}
